import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, AlertTriangle, FileWarning, Upload, FileText, XCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const credentialTypes = [
  "Resume",
  "Government ID",
  "Nursing License",
  "Social Worker Registration",
  "CPR Certification",
  "BLS Certification",
  "First Aid Certification",
  "Vulnerable Sector Check",
  "Immunization Records",
  "Professional Liability Insurance",
  "Other Credential",
];

function fileUrl(url) {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function statusClass(status) {
  if (status === "approved") return "bg-green-100 text-green-700";
  if (status === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

export default function ProfessionalProfile() {
  const { user } = useAuth();
  const [pro, setPro] = useState(null);
  const [editing, setEditing] = useState(false);
  const [credentialType, setCredentialType] = useState("Resume");
  const [credentialExpiry, setCredentialExpiry] = useState("");
  const [credentialFile, setCredentialFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);

  const load = async () => {
    if (!user?.professional_id) return;
    const { data } = await api.get(`/professionals/${user.professional_id}`);
    setPro(data);
  };

  useEffect(() => { load(); }, [user]);

  const save = async () => {
    try {
      const payload = { ...pro };
      delete payload.credentials;
      await api.patch(`/professionals/${pro.id}`, payload);
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch {
      toast.error("Failed to update profile");
    }
  };

  const uploadCredential = async () => {
    if (!credentialFile) {
      toast.error("Please choose a credential file.");
      return;
    }

    const fd = new FormData();
    fd.append("credential_type", credentialType);
    fd.append("expiry", credentialExpiry || "");
    fd.append("file", credentialFile);

    try {
      setUploading(true);
      const { data } = await api.post(`/professionals/${pro.id}/credentials`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPro(data);
      setCredentialFile(null);
      setCredentialExpiry("");
      toast.success("Credential uploaded for admin review.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Credential upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) {
      toast.error("Please choose a profile photo.");
      return;
    }

    const fd = new FormData();
    fd.append("file", photoFile);

    try {
      setUploadingPhoto(true);
      const { data } = await api.post(`/professionals/${pro.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPro(data);
      setPhotoFile(null);
      toast.success("Profile photo updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const uploadResume = async () => {
    if (!resumeFile) {
      toast.error("Please choose your resume file.");
      return;
    }

    const fd = new FormData();
    fd.append("file", resumeFile);

    try {
      setUploadingResume(true);
      const { data } = await api.post(`/professionals/${pro.id}/resume`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPro(data);
      setResumeFile(null);
      toast.success("Resume uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume upload failed.");
    } finally {
      setUploadingResume(false);
    }
  };

  if (!pro) return <div className="text-slate-500">Loading…</div>;

  const today = new Date();
  const credentials = pro.credentials || [];
  const expiringSoon = credentials.filter((c) => {
    if (!c.expiry) return false;
    const d = new Date(c.expiry);
    const daysLeft = (d - today) / (1000 * 60 * 60 * 24);
    return daysLeft < 90;
  });

  const verificationStatus = pro.verification_status || "pending_documents";
  const approved = verificationStatus === "approved";

  return (
    <div className="space-y-4" data-testid="pro-profile-page">
      <h1 className="text-2xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>My Profile</h1>

      <Card className={approved ? "border-green-200" : "border-amber-200"}>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            {approved ? (
              <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            )}
            <div>
              <div className="font-semibold text-slate-900">
                Credential Status: {verificationStatus.replaceAll("_", " ").toUpperCase()}
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {approved
                  ? "Your credentials are approved. You are eligible to accept job offers."
                  : "You must upload required credentials and wait for admin approval before accepting assignments."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="text-sm font-semibold text-slate-900">Profile Photo & Resume</div>

          <div className="flex flex-col md:flex-row gap-5">
            <div className="shrink-0">
              {pro.avatar_url ? (
                <img src={fileUrl(pro.avatar_url)} alt="Profile" className="w-28 h-28 rounded-2xl object-cover border border-slate-200" />
              ) : (
                <div className="w-28 h-28 rounded-2xl bg-brand-navy text-white flex items-center justify-center text-3xl font-bold">
                  {pro.first_name?.[0]}{pro.last_name?.[0]}
                </div>
              )}
            </div>

            <div className="flex-1 grid md:grid-cols-2 gap-4">
              <div>
                <Label>Upload Profile Photo</Label>
                <Input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
                <Button type="button" onClick={uploadPhoto} disabled={uploadingPhoto} className="mt-2 bg-brand-navy hover:bg-brand-blue">
                  {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                </Button>
              </div>

              <div>
                <Label>Upload Resume</Label>
                <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} />
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <Button type="button" onClick={uploadResume} disabled={uploadingResume} className="bg-brand-navy hover:bg-brand-blue">
                    {uploadingResume ? "Uploading..." : "Upload Resume"}
                  </Button>
                  {pro.resume_url && (
                    <a href={fileUrl(pro.resume_url)} target="_blank" rel="noreferrer" className="text-sm text-brand-navy hover:underline">
                      View Resume
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Personal</div>
            <Button size="sm" variant="ghost" onClick={() => editing ? save() : setEditing(true)} data-testid="pro-profile-edit-btn">
              {editing ? "Save" : "Edit"}
            </Button>
          </div>

          {editing ? (
            <div className="space-y-3">
              <div><Label>Phone</Label><Input value={pro.phone || ""} onChange={(e) => setPro({ ...pro, phone: e.target.value })} /></div>
              <div><Label>City</Label><Input value={pro.city || ""} onChange={(e) => setPro({ ...pro, city: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Hourly rate</Label><Input type="number" value={pro.hourly_rate || 0} onChange={(e) => setPro({ ...pro, hourly_rate: Number(e.target.value) })} data-testid="pro-edit-rate" /></div>
                <div><Label>Weekend rate</Label><Input type="number" value={pro.weekend_rate || 0} onChange={(e) => setPro({ ...pro, weekend_rate: Number(e.target.value) })} /></div>
              </div>
              <div><Label>Bio</Label><Textarea value={pro.bio || ""} onChange={(e) => setPro({ ...pro, bio: e.target.value })} /></div>
            </div>
          ) : (
            <div className="text-sm space-y-1 text-slate-700">
              <div>📞 {pro.phone}</div>
              <div>📍 {pro.city}, {pro.province}</div>
              <div>💼 {pro.primary_position} · {pro.experience_years}+ years</div>
              <div>💰 ${pro.hourly_rate}/hr · Weekend ${pro.weekend_rate}/hr</div>
              {pro.bio && <p className="text-slate-600 mt-2">{pro.bio}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Upload className="w-4 h-4 text-brand-teal" /> Upload Credential
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label>Credential Type</Label>
              <select
                className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm"
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value)}
              >
                {credentialTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input type="date" value={credentialExpiry} onChange={(e) => setCredentialExpiry(e.target.value)} />
            </div>
            <div>
              <Label>File</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setCredentialFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          <Button onClick={uploadCredential} disabled={uploading} className="bg-brand-navy hover:bg-brand-blue">
            {uploading ? "Uploading..." : "Submit for Review"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brand-teal" /> Credentials
          </div>

          <div className="space-y-2">
            {credentials.length === 0 && (
              <div className="text-sm text-slate-500">No credentials uploaded yet.</div>
            )}

            {credentials.map((c, i) => {
              const d = c.expiry ? new Date(c.expiry) : null;
              const daysLeft = d ? Math.floor((d - today) / (1000 * 60 * 60 * 24)) : null;
              const expiring = daysLeft != null && daysLeft < 90;

              return (
                <div key={c.id || i} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div>
                    <div className="text-sm font-medium">{c.type}</div>
                    <div className="text-xs text-slate-500">
                      {c.expiry ? `Expires ${c.expiry}` : "No expiry date"} · Uploaded {c.uploaded_at ? c.uploaded_at.slice(0, 10) : "—"}
                    </div>
                    {c.rejection_reason && (
                      <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> {c.rejection_reason}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {c.file_url && (
                      <a href={fileUrl(c.file_url)} target="_blank" rel="noreferrer" className="text-xs text-brand-navy hover:underline flex items-center gap-1">
                        <FileText className="w-3 h-3" /> View
                      </a>
                    )}
                    {expiring ? (
                      <span className="status-pill bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />{daysLeft}d</span>
                    ) : (
                      <span className={`status-pill ${statusClass(c.status)}`}>{c.status || "pending"}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {expiringSoon.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center gap-2">
              <FileWarning className="w-4 h-4" />
              {expiringSoon.length} credential{expiringSoon.length === 1 ? "" : "s"} expiring soon. Please update.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
